import React from "react";

import "./Blog.css";
import NavLight from "../../components/NavLight";
import BlogPageIntroImg from "../../assets/blog/startup-idea.png";
import BlogsPreview from "../../components/BlogsPreview";
import FoundersGuide from "../../components/FoundersGuide";
import {CustomMetaHelmet} from "../../components/CustomMetaHelmet";
import {meta} from "../../assets/meta/meta";

function HowToValidateStartupIdea() {
    return (
        <div className="Blog1">
            <CustomMetaHelmet meta={meta['how-to-validate-startup-idea']}/>
            <NavLight/>
            <section className="blog-page">
                <article className="blog-page-container">
                    <div>
                        <h1>Founder's guide to validating a B2B idea</h1>
                        <p>
                            <i>
                                For many founders, it's a bit of a puzzle to figure out how to validate a B2B idea.
                                However, in this step-by-step guide, I will walk you through how to not only validate
                                your idea but also obtain the first pilot customers to help you design your solution.
                            </i>
                        </p>
                    </div>
                    <div>
                        <figure>
                            <img
                                src={BlogPageIntroImg}
                                alt="purple hand pressing a digital button"/>
                        </figure>
                    </div>
                    <div>
                        <h2>Problem statement</h2>
                        <p>
                            As obvious as it might seem, you need to start by nailing the problem statement behind your
                            solution. The problem statement must be clear and concise. While it's tempting to focus on
                            validating your solution, it's important to validate the problem you are trying to solve
                            before attempting to validate the solution.
                            <p>&nbsp;</p>
                            For example, as a rule of thumb, you should ask five random people with no prior knowledge
                            of your idea to read your problem statement and describe how they understand the problem
                            statement presented.
                        </p>
                    </div>
                    <div>
                        <h2>Ideal customer profile</h2>
                        <p>
                            With a clear problem statement in hand, you then need to define a clear customer profile.
                            Make sure you have a detailed description of the characteristics that make up the most
                            profitable and valuable customers for your business idea. While it's tempting to focus on
                            more than one ICP, you have to constrain yourself and prioritize the most optimal one.
                            <p>&nbsp;</p>
                            For example, your ICP might be companies with 100-250 employees and an annual revenue of
                            $5-10 million in the software development industry located in North America. Furthermore,
                            the company is growth-oriented with a distributed workforce and tech-savvy business leaders
                            who are open to trying new solutions to increase productivity.
                        </p>
                    </div>
                    <div>
                        <h2>Schedule discovery user interviews</h2>
                        <p>
                            Next up, it's time to schedule user interviews to validate your problem statement with your
                            ICP. This is where your founder grit and persuasion skills will be put to the test. Identify
                            100 people in your ICP and contact them one by one, asking for a 20-minute user interview.
                            Of those 100 outreach emails, you should be able to land around 20 interviews.
                            <p>&nbsp;</p>
                            For example, ensure that you conduct thorough research on each person to write a relevant,
                            personal, and persuasive message. You are essentially a researcher seeking to interview a
                            domain expert. One way you can add value in exchange for their time is to promise them a
                            summary of everything you learned from speaking to multiple domain experts in the field
                            about the particular problem you are investigating.
                        </p>
                    </div>
                    <div>
                        <h2>Conduct the discovery user interviews</h2>
                        <p>
                            After scheduling around 20 interviews, your focus should be on validating the problem
                            statement and gaining as much knowledge as possible about the problem space. Prepare
                            questions that can help you prove or disprove your problem statement. Additionally, it's
                            essential to understand the priority of the problem, as companies prioritize high-impact
                            problems.
                            <p>&nbsp;</p>
                            For instance, if your problem statement assumes that X person spends 11 hours a month
                            manually completing a task, you need to ask questions to validate if they indeed perform
                            this task, how frequently, and how much time they spend on it. Ask a few times in various
                            ways to ensure that you get the complete picture. If you cannot validate the problem or it
                            is not a priority, ask them about their top three priorities in the company and the problems
                            that keep them up at night. Don't miss the opportunity to learn something new during your
                            user discovery interviews.
                        </p>
                    </div>
                    <div>
                        <h2>Validate the solution</h2>
                        <p>
                            After conducting 20 discovery user interviews, you should have at least five ideal customer
                            profiles (ICPs) that have validated the problem and expressed their willingness to solve it.
                            It's time to validate your B2B idea by adjusting it based on the feedback you received from
                            the interviews. Schedule a follow-up meeting with those five ICPs.
                            <p>&nbsp;</p>
                            For instance, ensure that you have mock-ups of the user flow of your solution. Walk the
                            person through each step of the flow and continuously ask for their feedback. Ask them
                            questions to identify if anything is missing for them to complete their task. Finally, ask
                            them questions to confirm if this is a solution they can get behind and use.
                        </p>
                    </div>
                    <div>
                        <h2>Pilot with early adopters</h2>
                        <p>
                            After validating the solution, choose the top three ICPs who require your solution the most
                            and are willing to start using it promptly. Prioritize need and speed to obtain early
                            adopters who are willing to accept a half-baked product and want to use it immediately.
                            Schedule a meeting with those three ICPs and propose a paid or unpaid pilot as design
                            customers.
                            <p>&nbsp;</p>
                            For example, ensure that you have iterated your mock-ups before proposing to run a pilot.
                            This will earn you goodwill and credibility when proposing to treat them as design customers
                            and adapt the product to their needs. Depending on their willingness to pay, you can propose
                            a paid pilot with an end date and agreed-upon pricing if they are satisfied after the
                            proposed end date.


                        </p>
                    </div>
                    <div>
                        <h2>Final words</h2>
                        <p>
                            As you can see, validating a B2B idea is not an easy process. However, in many ways, this
                            process also tests some of the most crucial traits that a founder needs to develop. It
                            involves defining your problem statement and ideal customers clearly and persuading
                            strangers to spare their valuable time for an interview. Ultimately, this process will help
                            you determine whether your B2B idea is feasible or not. Additionally, through the validation
                            process, you will obtain early adopters who genuinely need your solution and are willing to
                            pay for it.
                        </p>
                    </div>

                </article>
            </section>
            <section className="related-blogs">
                <h1> You might also like</h1>
                <BlogsPreview/>
            </section>
            <section className="FoundersGuide">
                <FoundersGuide/>
            </section>
        </div>
    );
}

export default HowToValidateStartupIdea;
